import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DurationBar from './DurationBar';
import type { ReconcilerApply } from '../../../../types/frontend';

export interface ExecutePhaseProps {
  execute: ReconcilerApply;
  maxMs: number;
}

const ExecutePhase: React.FC<ExecutePhaseProps> = ({ execute, maxMs }) => {
  const hasFail = execute.groups?.some((g) => g.actions.some((a) => a.status === 'failed'));
  const allMs = execute.groups?.flatMap((g) => g.actions.map((a) => a.duration_ms ?? 0)) ?? [];
  const localMax = Math.max(...allMs, 1);

  return (
    <Box sx={{ py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasFail ? (
            <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Execute</Typography>
        </Box>
        <DurationBar ms={execute.duration_ms} maxMs={maxMs} color={hasFail ? 'error.dark' : undefined} />
      </Box>

      <Box sx={{ mt: 1, pl: 3.5 }}>
        {execute.groups.map((group, gi) => (
          <Box key={gi} sx={{ mb: gi < execute.groups.length - 1 ? 1.25 : 0 }}>
            {execute.groups.length > 1 && (
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.625rem', mb: 0.25, display: 'block' }}
              >
                {group.label ?? `${group.actions.length} action${group.actions.length !== 1 ? 's' : ''}`}
              </Typography>
            )}
            {group.actions.map((action, ai) => (
              <Box key={ai} sx={{ mb: action.error ? 0.75 : 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {action.status === 'failed' ? (
                      <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: action.status === 'failed' ? 'error.light' : 'text.secondary' }}
                    >
                      {action.display_name}
                    </Typography>
                  </Box>
                  {action.duration_ms != null && (
                    <DurationBar
                      ms={action.duration_ms}
                      maxMs={localMax}
                      color={action.status === 'failed' ? 'error.dark' : 'text.disabled'}
                    />
                  )}
                </Box>
                {action.error && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                      mt: 0.5,
                      ml: 2.75,
                      py: 0.5,
                      px: 1.5,
                      '& .MuiAlert-icon': { fontSize: 16, mr: 1, py: 0.5 },
                      '& .MuiAlert-message': { py: 0 },
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {action.error}
                    </Typography>
                    {action.error_detail && action.error_detail !== action.error && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block', mt: 0.25, fontFamily: 'monospace', fontSize: '0.625rem', wordBreak: 'break-all' }}
                      >
                        {action.error_detail}
                      </Typography>
                    )}
                  </Alert>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ExecutePhase;
